import { useState, useMemo, useEffect } from "react";
import { ArrowLeft, Minus, Plus, ShoppingCart, Battery, Check, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import type { Product, ProductVariant } from "@/hooks/useProducts";
import { useCart } from "@/contexts/CartContext";

interface ProductDetailProps {
  product: Product;
  onBack: () => void;
}

const CONDITION_LABELS: Record<string, { label: string; description: string }> = {
  "A+": { label: "Come Nuovo", description: "Nessun segno di usura visibile" },
  "A": { label: "Eccellente", description: "Minimi segni di usura, quasi impercettibili" },
  "B": { label: "Ottimo", description: "Leggeri segni di usura, perfettamente funzionante" },
  "C": { label: "Buono", description: "Segni di usura visibili, funzionante al 100%" },
};

export const ProductDetail = ({ product, onBack }: ProductDetailProps) => {
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedMemory, setSelectedMemory] = useState<number | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<string | null>(null);
  const [selectedBattery, setSelectedBattery] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageFade, setImageFade] = useState(false);

  const { addItem } = useCart();

  // Get unique options from variants
  const colors = useMemo(() => {
    const colorMap = new Map<string, string>();
    product.variants?.forEach(v => {
      if (!colorMap.has(v.color)) colorMap.set(v.color, v.color_hex || '#000000');
    });
    return Array.from(colorMap.entries()).map(([name, hex]) => ({ name, hex }));
  }, [product.variants]);

  const memories = useMemo(() =>
    [...new Set(product.variants?.filter(v => v.memory_gb).map(v => v.memory_gb) || [])].sort((a, b) => (a || 0) - (b || 0)),
    [product.variants]
  );

  const CONDITION_ORDER = ['A+', 'A', 'B', 'C'];
  const conditions = useMemo(() =>
    [...new Set(product.variants?.map(v => v.condition_grade) || [])].sort((a, b) => {
      const ia = CONDITION_ORDER.indexOf(a);
      const ib = CONDITION_ORDER.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    }),
    [product.variants]
  );

  const batteryStatuses = useMemo(() => {
    const statuses = new Map<string, number>();
    product.variants?.forEach(v => {
      const key = `${v.battery_status || 'new'}-${v.battery_percentage || 100}`;
      if (!statuses.has(key)) {
        statuses.set(key, v.battery_percentage || 100);
      }
    });
    return Array.from(statuses.entries()).map(([key, percentage]) => ({
      status: key.split('-')[0],
      percentage,
      key,
    }));
  }, [product.variants]);

  // Auto-select first options
  useMemo(() => {
    if (!selectedColor && colors.length > 0) setSelectedColor(colors[0].name);
    if (!selectedMemory && memories.length > 0) setSelectedMemory(memories[0]);
    if (!selectedCondition && conditions.length > 0) setSelectedCondition(conditions[0]);
    if (!selectedBattery && batteryStatuses.length > 0) setSelectedBattery(batteryStatuses[0].key);
  }, [colors, memories, conditions, batteryStatuses]);

  const selectedBatteryInfo = useMemo(() => 
    batteryStatuses.find(b => b.key === selectedBattery),
    [batteryStatuses, selectedBattery]
  );

  // Find matching variant
  const selectedVariant = useMemo(() => {
    if (!selectedColor || !selectedCondition) return null;
    return product.variants?.find(v =>
      v.color === selectedColor &&
      v.condition_grade === selectedCondition &&
      (memories.length === 0 || v.memory_gb === selectedMemory) &&
      (!selectedBatteryInfo || (
        (v.battery_status || 'new') === selectedBatteryInfo.status &&
        (v.battery_percentage || 100) === selectedBatteryInfo.percentage
      ))
    ) || null;
  }, [product.variants, selectedColor, selectedMemory, selectedCondition, memories, selectedBatteryInfo]);

  // Calculate final price
  const baseDiscountedPrice = product.base_price * (1 - product.discount_percentage / 100);
  const finalPrice = useMemo(() => {
    const adjustment = selectedVariant?.price_adjustment || 0;
    return (baseDiscountedPrice + adjustment) * quantity;
  }, [baseDiscountedPrice, selectedVariant, quantity]);

  // Price breakdown
  const priceBreakdown = useMemo(() => {
    const variants = product.variants || [];
    if (variants.length === 0 || !selectedVariant) return null;

    const findVariant = (color: string, mem: number | null, cond: string) =>
      variants.find(v => v.color === color && v.memory_gb === mem && v.condition_grade === cond);

    const baseColor = colors[0]?.name;
    const baseMem = memories.length > 0 ? memories[0] : null;
    const baseCond = conditions[0];
    if (!baseColor || !baseCond) return null;

    const baseVariant = findVariant(baseColor, baseMem, baseCond);
    const baseAdj = baseVariant?.price_adjustment || 0;

    let capacityDelta = 0;
    if (selectedMemory && baseMem !== null && selectedMemory !== baseMem) {
      const capVariant = findVariant(baseColor, selectedMemory, baseCond);
      capacityDelta = (capVariant?.price_adjustment || 0) - baseAdj;
    }

    let colorDelta = 0;
    if (selectedColor && selectedColor !== baseColor) {
      const colorVariant = findVariant(selectedColor, baseMem, baseCond);
      colorDelta = (colorVariant?.price_adjustment || 0) - baseAdj;
    }

    let conditionDelta = 0;
    if (selectedCondition && selectedCondition !== baseCond) {
      const condVariant = findVariant(baseColor, baseMem, selectedCondition);
      conditionDelta = (condVariant?.price_adjustment || 0) - baseAdj;
    }

    const totalAdj = selectedVariant.price_adjustment || 0;
    const remainder = totalAdj - baseAdj - capacityDelta - colorDelta - conditionDelta;

    return { capacityDelta, colorDelta, conditionDelta, remainder, baseAdj };
  }, [product.variants, selectedVariant, selectedColor, selectedMemory, selectedCondition, colors, memories, conditions]);

  // Images filtered by selected color
  const allImages = product.images?.sort((a, b) => a.sort_order - b.sort_order) || [];
  
  const displayImages = useMemo(() => {
    const colorImages = selectedColor
      ? allImages.filter(img => img.variant_color === selectedColor)
      : [];
    const generalImages = allImages.filter(img => !img.variant_color);
    const result = colorImages.length > 0 ? colorImages : generalImages;
    
    if (result.length > 0) return result;
    return product.cover_image
      ? [{ id: '0', image_url: product.cover_image, is_cover: true, sort_order: 0, variant_color: null } as any]
      : [];
  }, [allImages, selectedColor, product.cover_image]);

  useEffect(() => {
    setImageFade(true);
    setSelectedImageIndex(0);
    const timer = setTimeout(() => setImageFade(false), 200);
    return () => clearTimeout(timer);
  }, [selectedColor]);

  const allOptionsSelected = selectedColor && selectedCondition && (memories.length === 0 || selectedMemory) && selectedBattery;
  const canAddToCart = allOptionsSelected && selectedVariant && selectedVariant.quantity >= quantity;
  const isOutOfStock = selectedVariant && selectedVariant.quantity === 0;

  const handleAddToCart = () => {
    if (!selectedVariant || !selectedColor || !selectedCondition) return;
    
    addItem({
      product,
      variant: selectedVariant,
      quantity,
      unitPrice: baseDiscountedPrice + (selectedVariant.price_adjustment || 0),
      selectedColor,
      selectedMemory: selectedMemory,
      selectedCondition,
    });
    
    toast.success(`${product.name} aggiunto al carrello`);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-semibold text-foreground truncate">{product.name}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className={`aspect-square bg-muted rounded-2xl overflow-hidden transition-opacity duration-200 ${imageFade ? 'opacity-0' : 'opacity-100'}`}>
              {displayImages[selectedImageIndex] ? (
                <img src={displayImages[selectedImageIndex].image_url} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">Nessuna immagine</div>
              )}
            </div>
            {displayImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {displayImages.map((img, index) => (
                  <button key={img.id} onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${selectedImageIndex === index ? 'border-primary' : 'border-border'}`}>
                    <img src={img.image_url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {product.category && <Badge variant="outline">{product.category.name}</Badge>}
                {product.discount_percentage > 0 && <Badge className="bg-destructive">-{product.discount_percentage}%</Badge>}
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-2">{product.name}</h2>
              {product.description && <p className="text-muted-foreground">{product.description}</p>}
            </div>

            {/* Price */}
            <div className="space-y-3">
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-bold text-foreground">€{(finalPrice / quantity).toFixed(2)}</span>
                {product.discount_percentage > 0 && (
                  <span className="text-xl text-muted-foreground line-through">€{product.base_price.toFixed(2)}</span>
                )}
                {quantity > 1 && (
                  <span className="text-sm text-muted-foreground">× {quantity} = €{finalPrice.toFixed(2)}</span>
                )}
              </div>

              {priceBreakdown && selectedVariant && (
                <div className="p-3 bg-muted/40 rounded-xl space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Prezzo base</span>
                    <span>€{baseDiscountedPrice.toFixed(2)}</span>
                  </div>
                  {selectedMemory && memories.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Capacità {selectedMemory}GB</span>
                      <span className={priceBreakdown.capacityDelta > 0 ? 'text-destructive' : priceBreakdown.capacityDelta < 0 ? 'text-green-600' : ''}>
                        {priceBreakdown.capacityDelta >= 0 ? '+' : ''}€{priceBreakdown.capacityDelta.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedColor && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Colore: {selectedColor}</span>
                      <span className={priceBreakdown.colorDelta > 0 ? 'text-destructive' : priceBreakdown.colorDelta < 0 ? 'text-green-600' : ''}>
                        {priceBreakdown.colorDelta >= 0 ? '+' : ''}€{priceBreakdown.colorDelta.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedCondition && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Condizione: {CONDITION_LABELS[selectedCondition]?.label || selectedCondition}</span>
                      <span className={priceBreakdown.conditionDelta > 0 ? 'text-destructive' : priceBreakdown.conditionDelta < 0 ? 'text-green-600' : ''}>
                        {priceBreakdown.conditionDelta >= 0 ? '+' : ''}€{priceBreakdown.conditionDelta.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {priceBreakdown.remainder !== 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Adeguamento combinato</span>
                      <span className={priceBreakdown.remainder > 0 ? 'text-destructive' : 'text-green-600'}>
                        {priceBreakdown.remainder >= 0 ? '+' : ''}€{priceBreakdown.remainder.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1.5 border-t border-border font-semibold">
                    <span>Totale unitario</span>
                    <span>€{(finalPrice / quantity).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Memory Selection */}
            {memories.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Capacità</Label>
                <div className="flex flex-wrap gap-2">
                  {memories.map(mem => (
                    <button key={mem} onClick={() => setSelectedMemory(mem)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border-2 transition-all ${
                        selectedMemory === mem
                          ? 'border-primary bg-primary text-primary-foreground shadow-md'
                          : 'border-border bg-background text-foreground hover:border-primary/50'
                      }`}>
                      {mem} GB
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {colors.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Colore {selectedColor && <span className="normal-case font-normal ml-1">— {selectedColor}</span>}
                </Label>
                <div className="flex flex-wrap gap-3">
                  {colors.map(color => (
                    <button key={color.name} onClick={() => setSelectedColor(color.name)}
                      className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center ${
                        selectedColor === color.name ? 'border-primary ring-2 ring-primary/30 scale-110' : 'border-border hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.name}>
                      {selectedColor === color.name && (
                        <Check className={`w-4 h-4 ${parseInt(color.hex.replace('#', ''), 16) < 0x888888 ? 'text-white' : 'text-black'}`} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Condition Selection */}
            {conditions.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Condizione</Label>
                <div className="grid grid-cols-2 gap-2">
                  {conditions.map(cond => {
                    const info = CONDITION_LABELS[cond] || { label: cond, description: "" };
                    return (
                      <button key={cond} onClick={() => setSelectedCondition(cond)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          selectedCondition === cond
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-border hover:border-primary/30'
                        }`}>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{info.label}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{cond}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{info.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Battery Status */}
            {batteryStatuses.length > 1 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Batteria</Label>
                <div className="flex flex-wrap gap-2">
                  {batteryStatuses.map(batt => (
                    <button key={batt.key} onClick={() => setSelectedBattery(batt.key)}
                      className={`px-4 py-2.5 rounded-xl border-2 text-left transition-all ${
                        selectedBattery === batt.key
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/30'
                      }`}>
                      <div className="flex items-center gap-2">
                        <Battery className={`w-4 h-4 ${batt.percentage >= 80 ? 'text-green-500' : 'text-orange-500'}`} />
                        <span className="font-semibold text-sm">{batt.status === 'new' ? 'Nuova' : 'Usata'}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{batt.percentage}%</Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Warranty */}
            {selectedVariant && (
              <Card className="bg-muted/50 border-border/50">
                <CardContent className="p-4 flex items-center gap-2 text-sm">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span>Garanzia <strong>{product.warranty_months || 12} mesi</strong></span>
                </CardContent>
              </Card>
            )}

            {/* Quantity */}
            {allOptionsSelected && selectedVariant && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Quantità</Label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-12 text-center font-semibold text-lg">{quantity}</span>
                    <Button variant="outline" size="icon" onClick={() => setQuantity(quantity + 1)} disabled={quantity >= selectedVariant.quantity}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <Badge variant={selectedVariant.quantity > 0 ? "outline" : "destructive"} className="text-sm">
                    {selectedVariant.quantity > 0 ? `${selectedVariant.quantity} disponibili` : 'Esaurito'}
                  </Badge>
                </div>
              </div>
            )}

            {/* Add to Cart */}
            <Button
              className="w-full h-14 text-lg gap-2 rounded-xl"
              disabled={!canAddToCart || isOutOfStock}
              onClick={handleAddToCart}
            >
              <ShoppingCart className="w-5 h-5" />
              {isOutOfStock ? 'Non disponibile' : 'Aggiungi al carrello'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
